import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionDescriptionRefs
 * @property {HTMLElement} content - Description content container
 * @property {HTMLButtonElement} toggleButton - Show more/less toggle
 * @property {HTMLElement} showMoreLabel - Show more label
 * @property {HTMLElement} showLessLabel - Show less label
 */

/**
 * Truncates long collection descriptions behind a show more toggle.
 * Full HTML content remains in the DOM on initial load.
 *
 * @extends {Component<CollectionDescriptionRefs>}
 */
class CollectionDescriptionComponent extends Component {
  requiredRefs = ['content', 'toggleButton', 'showMoreLabel', 'showLessLabel'];

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Re-checks overflow after dynamic content updates.
   */
  reset() {
    this.#init();
  }

  #init() {
    this.classList.remove('collection-description--expanded');

    const { content, toggleButton, showMoreLabel, showLessLabel } = this.refs;

    toggleButton.setAttribute('aria-expanded', 'false');
    showMoreLabel.classList.remove('hidden');
    showLessLabel.classList.add('hidden');

    requestAnimationFrame(() => {
      const isOverflowing = content.scrollHeight > content.clientHeight + 1;
      toggleButton.classList.toggle('hidden', !isOverflowing);
    });
  }

  /**
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();

    const expanded = this.classList.toggle('collection-description--expanded');
    const { toggleButton, showMoreLabel, showLessLabel } = this.refs;

    toggleButton.setAttribute('aria-expanded', String(expanded));
    showMoreLabel.classList.toggle('hidden', expanded);
    showLessLabel.classList.toggle('hidden', !expanded);
  }
}

if (!customElements.get('collection-description-component')) {
  customElements.define('collection-description-component', CollectionDescriptionComponent);
}

/**
 * @param {Element | Document} [root]
 */
export function resetCollectionDescription(root = document) {
  const component = root.querySelector('collection-description-component');

  if (component instanceof CollectionDescriptionComponent) {
    component.reset();
  }
}
